import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle2, XCircle, AlertCircle, Clock, Wifi, WifiOff } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

import { parseUtc } from '../lib/parseUtc';
function Agents() {
  const [agentStatus, setAgentStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAgentStatus = async () => {
    try {
      const response = await axios.get(`${API}/agent/status`);
      setAgentStatus(response.data);
    } catch (error) {
      toast.error('Failed to fetch agent status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgentStatus();
    const interval = setInterval(fetchAgentStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'disconnected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'not_configured':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'disconnected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'not_configured':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Never';

    const date = parseUtc(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="animate-pulse">
          <CardHeader>
            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <CardContent className="pt-6 pb-4">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">Agent Status</h1>
          <p className="text-slate-600 mt-2 text-lg flex items-center">
            <Wifi className="w-5 h-5 mr-2 text-blue-500" />
            Monitor agent connection status across all clusters
          </p>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 stagger-fade-in">
        <Card className="stat-card hover-lift transition-smooth border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 mb-3">Total Clusters</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">{agentStatus?.summary?.total_clusters || 0}</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-lg transition-transform hover:scale-110">
                <Wifi className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift transition-smooth border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 mb-3">Connected</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent">{agentStatus?.summary?.connected_agents || 0}</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg transition-transform hover:scale-110">
                <CheckCircle2 className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift transition-smooth border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 mb-3">Disconnected</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-red-600 to-rose-500 bg-clip-text text-transparent">{agentStatus?.summary?.disconnected_agents || 0}</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl shadow-lg transition-transform hover:scale-110">
                <WifiOff className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card hover-lift transition-smooth border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 mb-3">Not Configured</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-amber-600 to-orange-500 bg-clip-text text-transparent">{agentStatus?.summary?.not_configured || 0}</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg transition-transform hover:scale-110">
                <AlertCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent List */}
      <Card>
        <CardHeader>
          <CardTitle>Agent Status Details</CardTitle>
        </CardHeader>
        <CardContent>
          {agentStatus?.agent_status?.length > 0 ? (
            <div className="space-y-3">
              {agentStatus.agent_status.map((cluster) => (
                <div
                  key={cluster.cluster_id}
                  className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {getStatusIcon(cluster.agent_status)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{cluster.cluster_name}</h3>
                          <Badge variant="outline" className={`text-xs ${getStatusColor(cluster.agent_status)}`}>
                            {cluster.agent_status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          Cluster ID: <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{cluster.cluster_id}</code>
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-600">Last seen: {formatLastSeen(cluster.last_seen)}</span>
                          </div>
                          <div className="flex gap-3">
                            <span className={cluster.execution_methods.agent ? 'text-green-600' : 'text-gray-400'}>
                              Agent: {cluster.execution_methods.agent ? '✓' : '✗'}
                            </span>
                            <span className={cluster.execution_methods.direct_kubectl ? 'text-blue-600' : 'text-gray-400'}>
                              Direct: {cluster.execution_methods.direct_kubectl ? '✓' : '✗'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Wifi className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No agents configured</p>
              <p className="text-sm mt-1">Add clusters to see agent status</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Agents;
